import { inject, Service } from '@angular/core';
import {
  sentrySetUser,
  ThamService,
  UnleashContext,
  UnleashService,
} from '@thiememeulenhoff/ng-sdk';
import {
  AuthService,
  PlatformService,
  UserService,
} from '@thiememeulenhoff/ng-security';
import {
  LicenseCpi,
  LicenseState,
  UserInterface,
} from '@thiememeulenhoff/types';
import { firstValueFrom, switchMap } from 'rxjs';

@Service()
export class AuthenticationService {
  readonly #userService = inject(UserService);

  readonly #authService = inject(AuthService);

  readonly #unleashService = inject(UnleashService);

  readonly #platformService = inject(PlatformService);

  readonly #thamService = inject(ThamService);

  readonly user$ = this.#userService.user$;

  constructor() {
    this.#platformService.accessToken$.subscribe((accessToken) => {
      this.#thamService.accessToken = accessToken;
    });
  }

  login() {
    return this.#authService.login().pipe(switchMap(() => this.#loadUser()));
  }

  get user() {
    return this.#userService.user;
  }

  hasAdminLicense(user = this.user) {
    return !!user?.licenses.find((license) => {
      return (
        license.cpi === LicenseCpi.EditionAdmin &&
        license.state === LicenseState.Active
      );
    });
  }

  async #loadUser(renew = false) {
    const user = await firstValueFrom(this.#userService.fetchUser(renew));

    await this.#setupUnleashContext(user);
    sentrySetUser(user?.identifier);

    return user;
  }

  #previousContextString = '';

  // TODO: Make dry -> TLA-10715
  async #setupUnleashContext(user: UserInterface) {
    const nextContext = {
      [UnleashContext.UserReference]: user.identifier,
      [UnleashContext.Cpis]: user.licenses
        .map((license) => license.cpi)
        .join(','),
      ...(user.brin ? { [UnleashContext.BrinCode]: user.brin } : {}),
    };

    const nextContextString = JSON.stringify(nextContext);

    if (nextContextString !== this.#previousContextString) {
      await this.#unleashService.addContext(nextContext);
    }

    await this.#unleashService.start();

    this.#previousContextString = nextContextString;
  }
}
